<template>
  <div id="call-records-view" class="page-table scrollable">
    <el-dialog
      width="440px"
      :visible="modalVisible"
      :before-close="handleCloseModal"
      :close-on-click-modal="false"
    >
      <h2 class="form-section-header">Filter Settings</h2>

      <h3 class="black-text mb-5">Select Date Range</h3>
      <el-date-picker
        class="mb-10"
        style="margin-left: auto"
        v-model="historyDateRange"
        type="datetimerange"
        align="right"
        unlink-panels
        format="yyyy-MM-dd HH:mm"
        :clearable="false"
        range-separator="To"
        start-placeholder="Start date"
        end-placeholder="End date"
        :picker-options="datePickerOptions"
      >
      </el-date-picker>

      <el-form id="stats-advanced-modal" class="form-modal">
        <h3 class="black-text mb-10">Select Distribution</h3>
        <el-radio-group v-model="distribution">
          <el-radio-button label="hourly">Hourly</el-radio-button>
          <el-radio-button type="default" label="daily">Daily</el-radio-button>
          <el-radio-button label="monthly">Monthly</el-radio-button>
          <el-radio-button label="agents">Agents</el-radio-button>
          <el-radio-button label="none">None</el-radio-button>
        </el-radio-group>

        <h3 class="black-text mb-0">Hold Time (Minutes)</h3>

        <el-input
          placeholder="Enter Hold Time in Minutes"
          v-model="holdTime"
        />
        <template slot="prefix"> Hold Time </template>

        <h3 class="black-text mb-0">Filter By</h3>
        <el-select
          v-model="primaryFilter"
          placeholder="Select Filter"
          clearable
          style="margin-top: 0"
        >
          <template slot="prefix"> Filter By </template>
          <el-option value="queues" label="Queues" />
          <el-option value="tag" label="Tag" />
        </el-select>

        <el-select
          v-model="filterQueues"
          v-if="primaryFilter === 'queues'"
          value-key="id"
          placeholder="Select Queues"
          clearable
          multiple
          filterable
          style="margin-top: 0"
        >
          <el-option
            v-for="item in queueOptions"
            :key="item.id"
            :value="item.id"
            :label="item.name"
          />
        </el-select>
        <el-input
          v-if="primaryFilter === 'tag'"
          placeholder="Enter Call Tag"
          v-model="tag"
        >
          <template slot="prefix"> Call Tag </template>
        </el-input>
        <el-button class="mt-20" @click="doAdvanced"> Update </el-button>
      </el-form>
    </el-dialog>

    <div id="users-stats-view" class="no-table-box page-header">
      <div class="flex justify-space-between mb-20">
        <h2 class="mb-0 mt-0 text-left">
          <v-svg sprite="queue" class="voxo-red"></v-svg>
          Queue Summary
        </h2>
        <el-tag>
          <i class="el-icon-info" style="margin-right: 4px"></i>
          Total Queues: {{ total }}
        </el-tag>
      </div>

      <div class="flex align-center mb-20">
        <el-radio-group
          v-model="presetSelection"
          @change="doPreset"
          class="hidden-xs-only"
        >
          <el-radio-button type="default" label="yesterday"
            >Yesterday</el-radio-button
          >
          <el-radio-button type="default" label="last10"
            >Last 10 Days</el-radio-button
          >
          <el-radio-button label="lastMonth">Last 30 Days</el-radio-button>
        </el-radio-group>

        <el-tooltip placement="top">
          <template slot="content">
            {{ selectedDateRange }}
            <br />
            Dist: {{ distribution[0].toUpperCase() + distribution.slice(1) }}
          </template>
          <el-button @click="modalVisible = true" class="ml-10">
            Filter
          </el-button>
        </el-tooltip>
        <el-button
          @click="resetTable"
          v-if="advancedActive"
          style="background-color: #f25a6b; color: #fff"
        >
          Reset
        </el-button>

        <el-dropdown class="ml-10">
          <el-button>
            Export
            <i class="el-icon-arrow-down el-icon--right"></i>
          </el-button>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item @click.native="doExport">
              <v-svg class="success-text" sprite="list" />
              CSV
            </el-dropdown-item>
            <el-dropdown-item @click.native="openScheduleReportModal">
              <v-svg class="voxo-orange" sprite="clock" />
              Schedule Report
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>

      <div class="vxe-table-height-tabs card-base card-shadow--medium">
        <vxe-grid
          round
          class="user-stats-table"
          border="inner"
          ref="xGrid"
          height="100%"
          auto-resize
          empty-text="No Data"
          :scroll-y="{ enabled: true, gt: 50, oSize: 0 }"
          :loading="loadingData"
          :columns="
            this.distribute
              ? [this.expandColumn, ...this.tableColumns]
              : this.tableColumns
          "
        >
        </vxe-grid>
      </div>
    </div>

    <schedule-report-modal
      :modalVisible="scheduleReportVisible"
      :filterSettings="filterSettings"
      @cancelOperation="scheduleReportVisible = false"
    />
  </div>
</template>

<script>
import feathersClient from "@/feathers/feathers-client";
import ScheduleReportModal from "@/components/schedule-reports/ScheduleReportModal";
import moment from "moment-timezone";
import { ExportToCsv } from "export-to-csv";

export default {
  name: "QueueSummary",
  props: {
    queueOptions: { required: true },
  },

  components: {
    ScheduleReportModal,
  },

  data() {
    return {
      scheduleReportVisible: false,
      queryParams: null,
      filterSettings: null,

      exportCSV: false,
      distribute: false,
      presetSelection: "last10",
      advancedActive: false,
      loadingData: false,
      modalVisible: false,
      queueIds: [],
      filterQueues: [],
      tableData: [],
      noQueues: true,
      dateStart: null,
      dateEnd: null,
      tag: "",
      holdTime: 0.5,
      primaryFilter: null,
      distribution: "none",
      total: 0,
      historyDateRange: [moment(), moment()],
      datePickerOptions: {
        shortcuts: [
          {
            text: "Last week",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(1, "week").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last month",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(1, "month").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last 3 months",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(3, "months").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last 6 months",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(6, "months").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last year",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(12, "months").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
        ],
      },
      expandColumn: {
        type: "expand",
        width: "25px",
        showOverflow: true,
        slots: {
          content: ({ row }) => {
            return [
              <div class="users-stats-distribution-table">
                <vxe-table
                  border="none"
                  show-overflow
                  highlight-hover-row
                  show-header={false}
                  height="300"
                  empty-text="No Data"
                  scroll-y={{enabled: true, gt: 10, oSize: 0}}
                  data={row.distributionStats}
                >
                  <vxe-table-column field="name" width="190"></vxe-table-column>
                  <vxe-table-column
                    field="totalComplete"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="totalAbandoned"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="totalRefused"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="blindTransfers"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="attendedTransfers"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="totalTransfers"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="totalWaitTime"
                    min-width="100"
                    formatter={this.timeFormatterTable}
                  ></vxe-table-column>
                  <vxe-table-column
                    field="avgWaitTime"
                    min-width="100"
                    formatter={this.timeFormatterTable}
                  ></vxe-table-column>
                  <vxe-table-column
                    field="shortestWait"
                    min-width="100"
                    formatter={this.waitFormat}
                  ></vxe-table-column>
                  <vxe-table-column
                    field="longestWait"
                    min-width="100"
                    formatter={this.waitFormat}
                  ></vxe-table-column>
                  <vxe-table-column
                    field="percentUnder30"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="totalTalkTime"
                    min-width="100"
                    formatter={this.timeFormatterTable}
                  ></vxe-table-column>
                  <vxe-table-column
                    field="avgTalkTime"
                    min-width="100"
                    formatter={this.timeFormatterTable}
                  ></vxe-table-column>
                  <vxe-table-column
                    field="exitKey"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="exitTimeout"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="totalInbound"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="totalUnique"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="adjustedAnswerRate"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="avgAbandonWaitTime"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="completeCallbacks"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="successfullCallbacks"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="failedCallbacks"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="underHoldTime"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="overHoldTime"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="outboundResults"
                    min-width="100"
                  ></vxe-table-column>
                </vxe-table>
              </div>,
            ];
          },
        },
      },
      tableColumns: [
        {
          field: "name",
          title: "Name",
          width: "190",
          showOverflow: true,
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.name}</span>];
            },
          },
        },
        {
          field: "totalComplete",
          title: "Complete",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.totalComplete}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Inbound Complete">
                  <v-svg sprite="call-inbound" class="voxo-green"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "totalAbandoned",
          title: "Abandoned",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.totalAbandoned}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Abandoned">
                  <v-svg sprite="call-inbound" class="voxo-red"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "totalRefused",
          title: "Refused",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.totalRefused}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Refused">
                  <v-svg sprite="call-block" class="voxo-dark-red"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "blindTransfers",
          title: "Blind Transfers",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.blindTransfers}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Blind Transfers">
                  <v-svg sprite="call-forward" class="voxo-purple"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "attendedTransfers",
          title: "Attended Transfers",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.attendedTransfers}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Attended Transfers">
                  <v-svg sprite="call-forward" class="voxo-green"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "totalTransfers",
          title: "Total Transfers",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.totalTransfers}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Total Transfers">
                  <v-svg sprite="call-forward" class="voxo-blue"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "totalWaitTime",
          title: "Total Wait Time",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{this.timeFormatter(row.totalWaitTime)}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Total Wait Time">
                  <v-svg sprite="clock" class="voxo-dark-red"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "avgWaitTime",
          title: "Average Wait Time",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{this.timeFormatter(row.avgWaitTime)}</span>];
            },
            header: () => {
              return [
                <el-tooltip
                  placement="top"
                  content="Average Wait Time Per Call"
                >
                  <v-svg sprite="clock" class="voxo-orange"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "shortestWait",
          title: "Shortest Wait Time",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{this.timeFormatter(row.shortestWait)}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Shortest Wait">
                  <v-svg sprite="clock" class="voxo-red"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "longestWait",
          title: "Longest Wait Time",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{this.timeFormatter(row.longestWait)}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Longest Wait">
                  <v-svg sprite="clock" class="voxo-dark-red"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "percentUnder30",
          title: "Answer Speed",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.percentUnder30}%</span>];
            },
            header: () => {
              return [
                <el-tooltip
                  placement="top"
                  content="Percent Answered Under 30 Seconds"
                >
                  <v-svg sprite="clock" class="voxo-green"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "totalTalkTime",
          title: "Total Talk Time",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{this.timeFormatter(row.totalTalkTime)}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Total Talk Time">
                  <v-svg sprite="clock" class="voxo-blue"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "avgTalkTime",
          title: "Average Talk Time",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{this.timeFormatter(row.avgTalkTime)}</span>];
            },
            header: () => {
              return [
                <el-tooltip
                  placement="top"
                  content="Average Talk Time Per Call"
                >
                  <v-svg sprite="clock" class="voxo-aqua"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "exitKey",
          title: "Exit By Key Press",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.exitKey}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Exits With Key Press">
                  <v-svg sprite="arrow-outbound" class="voxo-pink"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "exitTimeout",
          title: "Exit By Timeout",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.exitTimeout}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Exits By Timeout">
                  <v-svg sprite="arrow-outbound" class="voxo-dark-red"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "totalInbound",
          title: "Total Inbound",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.totalInbound}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Total Inbound">
                  <v-svg sprite="call-inbound" class="voxo-aqua"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "totalUnique",
          title: "Unique Inbound",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.totalUnique}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Unique Inbound">
                  <v-svg sprite="call-inbound" class="voxo-blue"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "adjustedAnswerRate",
          title: "Adjusted Answer Rate",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.adjustedAnswerRate}%</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Answer Rate Minus Abandons">
                  <v-svg sprite="arrow-inbound" class="voxo-orange"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "avgAbandonWaitTime",
          title: "Avg Time of Abandon",
          minWidth: 100,
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.avgAbandonWaitTime}</span>];
            },
            header: () => {
              return [
                <el-tooltip
                  placement="top"
                  content="Avg Abandon Time Per Call (s)"
                >
                  <v-svg sprite="clock" class="voxo-dark-red"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "completeCallbacks",
          title: "Complete Callbacks",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.completeCallbacks}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Complete Callbacks">
                  <v-svg sprite="call-inbound" class="voxo-blue"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "successfulCallbacks",
          title: "Successful Callbacks",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.successfulCallbacks}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Successful Callbacks">
                  <v-svg sprite="call-inbound" class="voxo-green"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "failedCallbacks",
          title: "Failed Callbacks",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.failedCallbacks}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Failed Callbacks">
                  <v-svg sprite="call-inbound" class="voxo-red"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "underHoldTime",
          title: "Under",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.underHoldTime}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content={ this.underHoldTip }>
                  <v-svg sprite="clock" class="voxo-green"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "overHoldTime",
          title: "Over",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.overHoldTime}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content={ this.overHoldTip }>
                  <v-svg sprite="clock" class="voxo-red"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "outboundResults",
          title: "Outbound Results",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.outboundResults}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Total Outbound">
                  <v-svg sprite="call-outbound" class="voxo-green"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
      ],
    };
  },

  computed: {
    user() {
      return this.$store.state.auth.user;
    },
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },
    selectedDateRange() {
      if (this.dateEnd && this.dateStart) {
        const dateStart = moment(this.dateStart).format("M/D/YY HH:mm");
        const dateEnd = moment(this.dateEnd).format("M/D/YY HH:mm");
        return `${dateStart} - ${dateEnd}`;
      }
    },
  },

  watch: {
    selectedTenant() {
      this.loadingData = true;
    },

    queryParams(params) {
      if (params) {
        const {
          type,
          tenantId,
          tenantCode,
          primaryFilter,
          queueIds,
          tag,
          distribution,
          timezone,
        } = params;

        const queryParams = {
          type,
          tenantId: {
            label: this.selectedTenant.name,
            value: tenantId,
          },
          tenantCode,
          primaryFilter: {
            label: "",
            value: "",
          },
          queueIds: [],
          tag,
          distribution,
          timezone,
        };

        if (primaryFilter) {
          const primaryFilterMap = {
            queues: "Queues",
            tag: "Call Tag",
          };

          queryParams["primaryFilter"] = {
            label: primaryFilterMap[primaryFilter],
            value: primaryFilter,
          };
        }

        if (queueIds.length) {
          queryParams.queueIds = this.queueOptions
            .filter((queue) => queueIds.includes(queue.id))
            .map(({ id, name }) => ({ label: name, value: id }));
        }

        this.filterSettings = {
          servicePath: "account-statistics",
          query: queryParams,
        };
      }
    },
  },

  methods: {
    openScheduleReportModal() {
      this.scheduleReportVisible = true;
    },

    initData() {
      if (this.queueOptions.length) {
        this.noQueues = false;
        this.queueIds = this.queueOptions.map((item) => item.id);
        this.underHoldTip = "Hold Time Under " + this.holdTime + " minutes";
        this.overHoldTip = "Hold Time Over " + this.holdTime + " minutes";
        this.doPreset("init");
      } else {
        if (this.chart) {
          this.chart.dispose();
        }
        this.noQueues = true;
      }
    },
    updateDateRangePicker() {
      this.historyDateRange = [moment(this.dateStart), moment(this.dateEnd)];
      this.underHoldTip = "Hold Time Under " + this.holdTime + " minutes";
      this.overHoldTip = "Hold Time Over " + this.holdTime + " minutes";
    },
    doPreset(val) {
      if (val === "init") {
        this.dateEnd = moment(moment().format("YYYY-MM-DD"))
          .add(1, "day")
          .subtract(1, "minute")
          .format("YYYY-MM-DD HH:mm");
        this.dateStart = moment().startOf("day").format("YYYY-MM-DD");
        this.presetSelection = "init";
        this.updateDateRangePicker();
        this.queryStats();
      }

      if (val === "last10") {
        this.dateEnd = moment(moment().format("YYYY-MM-DD"))
          .add(1, "day")
          .subtract(1, "minute")
          .format("YYYY-MM-DD HH:mm");
        this.dateStart = moment().subtract(9, "days").format("YYYY-MM-DD");
        this.presetSelection = "last10";
        this.updateDateRangePicker();
        this.queryStats();
      }

      if (val === "lastMonth") {
        this.dateEnd = moment(moment().format("YYYY-MM-DD"))
          .add(1, "day")
          .subtract(1, "minute")
          .format("YYYY-MM-DD HH:mm");
        this.dateStart = moment().subtract(30, "days").format("YYYY-MM-DD");
        this.presetSelection = "lastMonth";
        this.updateDateRangePicker();
        this.queryStats();
      }

      if (val === "yesterday") {
        this.dateEnd = moment()
          .startOf("day")
          .subtract(1, "minute")
          .format("YYYY-MM-DD HH:mm");
        this.dateStart = moment()
          .subtract(1, "day")
          .startOf("day")
          .format("YYYY-MM-DD HH:mm");
        this.presetSelection = "yesterday";
        this.updateDateRangePicker();
        this.queryStats();
      }
    },
    handleCloseModal() {
      this.modalVisible = false;
    },
    doAdvanced() {
      this.dateStart = moment(this.historyDateRange[0]).format(
        "YYYY-MM-DD HH:mm:ss"
      );
      this.dateEnd = moment(this.historyDateRange[1]).format(
        "YYYY-MM-DD HH:mm:ss"
      );
      //if hourly distribution make sure it passes 30 day limit
      let allowed = 1;
      if (this.distribution === "hourly") {
        allowed =
          moment(this.dateEnd).diff(
            moment(this.dateStart).add(1, "day"),
            "days"
          ) <= 2;
      }

      if (allowed) {
        this.updateDateRangePicker();
        this.queryStats();
        this.handleCloseModal();
        this.advancedActive = true;
        this.distribute = this.distribution !== "none";
        this.presetSelection = null;
      } else {
        this.$notify({
          type: "error",
          message: "Max of 2 days exceeded for hourly distribution",
          title: "Limit Exceeded",
        });
      }
    },
    resetTable(tenantChanged) {
      this.advancedActive = false;
      this.primaryFilter = "";
      this.filterQueues = [];
      this.tag = "";
      this.holdTime = 0.5,
      this.exportCSV = false;
      this.distribution = "none";
      this.distribute = false;
      if (tenantChanged) {
        this.initData();
      } else {
        this.doPreset("init");
      }
    },
    doExport() {
      this.$confirm(`Export data for ${this.total} queues?`, {
        type: "warning",
      })
        .then(() => {
          //export from browser
          let csvData = [];
          for (let i = 0; i < this.tableData.length; i++) {
            const exclude = [
              "noLoggedInAgentEvents",
              "allAgentsBusyEvents",
              "_XID",
              "distributionStats",
              "totalAbandonWaitTime",
            ];

            const csvObject = {};
            for (let key in this.tableData[i]) {
              if (!exclude.includes(key)) {
                csvObject[key] =
                  this.tableData[i][key] === null ? 0 : this.tableData[i][key];
              }
            }
            csvData.push(csvObject);

            const distStats = this.tableData[i].distributionStats;
            if (distStats) {
              for (let k = 0; k < distStats.length; k++) {
                const csvObjectDist = {};
                for (let key in distStats[k]) {
                  if (!exclude.includes(key)) {
                    csvObjectDist[key] =
                      distStats[k][key] === null ? 0 : distStats[k][key];
                  }
                }
                if (Object.keys(csvObjectDist).length)
                  csvData.push(csvObjectDist);
              }
            }
          }

          const options = {
            filename: "queue_summary",
            showLabels: true,
            headers: [
              "Name",
              "Inbound Complete",
              "Abandoned",
              "Refused",
              "Blind Transfers",
              "Attended Transfers",
              "Total Transfers",
              "Total Wait Time",
              "Average Wait Time Per Call",
              "Shortest Wait",
              "Longest Wait",
              "Answered Under 30 Seconds",
              "Percent Answered Under 30 Seconds",
              "Total Talk Time",
              "Average Talk Time Per Call",
              "Exits With Key Press",
              "Exits By Timeout",
              "Total Inbound",
              "Answer Rate Minus Abandons",
              "Avg Abandon Wait Time Per Call (s)",
              "Unique Total",
              "Complete Callbacks",
              "Successful Callbacks",
              "Failed Callbacks",
              "Hold Time Under " + this.holdTime + " minutes",
              "Hold Time Over " + this.holdTime + " minutes",
              "Total Outbound"
            ],
          };

          const csvExporter = new ExportToCsv(options);
          csvExporter.generateCsv(csvData);
        })
        .catch(() => true);
    },
    queryStats() {
      this.loadingData = true;
      this.queryParams = {
        type: "queueSummary",
        tenantId: this.selectedTenant.id,
        tenantCode: this.selectedTenant.tenantCode,
        primaryFilter: this.primaryFilter,
        queueIds:
          this.primaryFilter === "queues" ? this.filterQueues : this.queueIds,
        tag: this.tag,
        distribution: this.distribution,
        timezone: this.user.timezone,
      };

      feathersClient
        .service("account-statistics")
        .create({
          holdTime: this.holdTime,
          dateStart: this.dateStart,
          dateEnd: this.dateEnd,
          exportCSV: this.exportCSV,
          ...this.queryParams,
        })
        .then((res) => {
          if (!this.exportCSV) {
            this.total = res.total;
            this.tableData = res.data;
            this.pages = Math.ceil(res.total / this.limit);
            this.page = this.page > this.pages ? 1 : this.page;
            this.populateTable();
          }

          if (this.exportCSV) {
            this.downloadCSV(res);
          }
          this.exportCSV = false;
          this.loadingData = false;
        })
        .catch(() => {
          console.log("Could Not Process Stats Request");
          this.loadingData = false;
        });
    },
    downloadCSV(data) {
      const fileBlob = new Blob([data.file]);
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(fileBlob);
      link.download = data.downloadName;
      link.click();
    },
    timeFormatter(value) {
      return moment.duration(value, "seconds").format("HH:mm:ss", {
        trim: false,
      });
    },
    timeFormatterTable(data) {
      return this.timeFormatter(data.cellValue);
    },
    waitFormat(data) {
      return data.cellValue ? this.timeFormatter(data.cellValue) : "-";
    },
    populateTable() {
      if (this.$refs.xGrid) {
        this.$refs.xGrid.loadData(this.tableData);
        const arrowDown = document.querySelectorAll(".vxe-sort--desc-btn");
        const arrowUp = document.querySelectorAll(".vxe-sort--asc-btn");
        const arrows = [...arrowDown, ...arrowUp];
        for (let i = 0; i < arrows.length; i++) {
          arrows[i].removeAttribute("title");
        }
      }
    },
  },
};
</script>
